/* =====================================================================================
   Authentication.

   Two deliberate properties.

   1. WITHOUT SUPABASE THERE IS NO SIGN-IN. `BACKEND === 'local'` means the application is
      running on seed data with no real records in it, so demanding credentials would be
      theatre. The gate appears the moment a backend exists and not before, which keeps
      `npm run dev` working for anyone who has not provisioned a project.

   2. THE SESSION IS THE ONLY THING THIS FILE TRUSTS. It holds no roles and makes no access
      decisions. Every question of the form "may this person see this" is answered by Row
      Level Security in Postgres, because a check performed in the browser is a suggestion.
      What lives here is who is signed in, not what they are allowed to do.

   Step-up re-authentication for Notebook and Locker lives in components/SuiteLauncher.tsx.
   Signing in to Forum deliberately does NOT unlock those two.
   ===================================================================================== */

import {
  createContext, useContext, useEffect, useState, useCallback, type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { BACKEND } from './db';

export interface Member {
  id: string;
  full_name: string | null;
  email: string | null;
  job_title: string | null;
}

interface AuthState {
  /** Null while the initial session lookup is still in flight. */
  ready: boolean;
  /** True when no backend is configured, so the gate is bypassed by design. */
  demo: boolean;
  session: Session | null;
  member: Member | null;
  signInWithPassword(email: string, password: string): Promise<void>;
  sendMagicLink(email: string): Promise<void>;
  signOut(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

/* Supabase returns deliberately vague errors for bad credentials, which is correct: a
   message distinguishing "no such account" from "wrong password" tells an attacker which
   addresses are registered. These translations keep that property while giving a real
   person something more useful than a status code. */
function readable(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials')) {
    return 'That email address and password do not match an account.';
  }
  if (m.includes('email not confirmed')) {
    return 'This address has not been confirmed yet. Use the sign-in link instead.';
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  if (m.includes('failed to fetch') || m.includes('network')) {
    return 'Could not reach the server. Check your connection.';
  }
  return message;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const demo = BACKEND === 'local';
  const [ready, setReady] = useState(demo);
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<Member | null>(null);

  useEffect(() => {
    if (demo) return;
    let live = true;

    (async () => {
      const { supabase } = await import('./supabase');
      const { data } = await supabase().auth.getSession();
      if (!live) return;
      setSession(data.session);
      setReady(true);

      // Fires on sign-in, sign-out and silent token refresh, so a session that expires in
      // another tab does not leave this one showing a signed-in shell over dead credentials.
      const { data: sub } = supabase().auth.onAuthStateChange((_e, s) => {
        if (live) setSession(s);
      });
      return () => sub.subscription.unsubscribe();
    })();

    return () => { live = false; };
  }, [demo]);

  // The app.members row, which carries the name and job title. Its absence is meaningful:
  // an auth user with no member record has authenticated but belongs to no organisation, so
  // RLS will correctly show them nothing at all.
  useEffect(() => {
    if (demo || !session) { setMember(null); return; }
    let live = true;
    (async () => {
      // An invited person has a member row with no auth account attached until they sign in.
      // This links the two, and is a cheap no-op on every subsequent load.
      try {
        const { claimMembership } = await import('./crew');
        await claimMembership();
      } catch { /* never block sign-in on this */ }
      const { currentMember } = await import('./supabase');
      try {
        const m = await currentMember();
        if (live) setMember(m as Member | null);
      } catch {
        if (live) setMember(null);
      }
    })();
    return () => { live = false; };
  }, [demo, session]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { supabase } = await import('./supabase');
    const { error } = await supabase().auth.signInWithPassword({
      email: email.trim(), password,
    });
    if (error) throw new Error(readable(error.message));
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    const { supabase } = await import('./supabase');
    const { error } = await supabase().auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw new Error(readable(error.message));
  }, []);

  const signOut = useCallback(async () => {
    const { supabase } = await import('./supabase');
    await supabase().auth.signOut();
    setSession(null);
    setMember(null);
    // Step-up grants for Locker and Notebook are held in sessionStorage. Leaving them behind
    // would let the next person at this machine reach the two most sensitive applications
    // without authenticating at all.
    sessionStorage.clear();
  }, []);

  return (
    <Ctx.Provider value={{
      ready, demo, session, member, signInWithPassword, sendMagicLink, signOut,
    }}>
      {children}
    </Ctx.Provider>
  );
}
